// src/pages/ProjectApplicationPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import { projectService } from '../api';
import useApi from '../hooks/useApi';
import SectionTitle from '../components/common/SectionTitle';
import Button from '../components/common/Button';

// Styled components remain the same
const ApplicationSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Breadcrumbs = styled.div`
  margin-bottom: 2rem;
  color: ${props => props.theme.text};
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  span {
    margin: 0 0.5rem;
  }
`;

const ProjectSummary = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 1.5rem;
  margin-bottom: 2rem;
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ProjectImage = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 8px;
  background-image: url(${props => props.src || '/default-project.jpg'});
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ProjectInfo = styled.div`
  flex: 1;
`;

const ProjectTitle = styled.h2`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
`;

const ProjectStatus = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'planning':
        return props.theme.accent;
      case 'in_progress':
        return props.theme.primary;
      case 'completed':
        return props.theme.success;
      case 'on_hold':
        return '#f59e0b';
      default:
        return props.theme.text;
    }
  }};
  margin-left: 1rem;
`;

const ProjectDescription = styled.p`
  color: ${props => props.theme.text};
  margin: 1rem 0;
`;

const TechTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.8rem;
`;

const ApplicationForm = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 8px;
  box-shadow: ${props => props.theme.card.shadow};
  padding: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.8rem;
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  font-family: 'Open Sans', sans-serif;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: 0.9rem;
  margin-top: 0.3rem;
`;

const SuccessMessage = styled(motion.div)`
  background-color: ${props => props.theme.success};
  color: white;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const LoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
`;

const ProjectApplicationPage = () => {
  const { projectSlug } = useParams();
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [applicationSuccess, setApplicationSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Charger les détails du projet
  const { 
    data: project, 
    loading: projectLoading, 
    error: projectError 
  } = useApi(() => projectService.getById(projectSlug), [projectSlug]);
  
  // Mise à jour du titre de la page
  useEffect(() => {
    if (project) {
      document.title = `${t('applyTo')} ${project.title} | PropentaTech`;
    }
    return () => {
      document.title = 'PropentaTech';
    };
  }, [project, t]);
  
  // Vérifier si le projet est ouvert aux candidatures
  useEffect(() => {
    if (project && !project.is_open_for_applications) {
      navigate(`/projects/${projectSlug}`);
    }
  }, [project, navigate, projectSlug]);
  
  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required(t('nameRequired')),
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    phone: Yup.string().required(t('phoneRequired')),
    motivation: Yup.string().required(t('motivationRequired')).min(30, t('motivationMinLength')),
    experience: Yup.string().required(t('experienceRequired'))
  });
  
      // Configuration du formulaire
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      motivation: '',
      experience: ''
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Préparer les données pour l'API apply - sans le champ experience
        const applicationData = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          motivation: values.motivation + "\n\nExpérience pertinente: " + values.experience
        };
        
        // Envoyer la candidature en utilisant l'endpoint apply
        const response = await projectService.apply(project.id, applicationData);
        console.log("Application response:", response);
        
        setApplicationSuccess(true);
        resetForm();
        
        // Rediriger vers la page du projet après quelques secondes
        setTimeout(() => {
          navigate(`/projects/${projectSlug}`);
        }, 5000);
      } catch (error) {
        console.error("Error submitting application:", error);
        setError(error.response?.data?.detail || t('applicationError'));
      } finally {
        setSubmitting(false);
      }
    }
  });
  
  // Traduction du statut du projet
  const getStatusLabel = (status) => {
    switch (status) {
      case 'planning':
        return t('planning');
      case 'in_progress':
        return t('inProgress');
      case 'completed':
        return t('completed');
      case 'on_hold':
        return t('onHold');
      default:
        return status;
    }
  };
  
  if (projectLoading) {
    return (
      <ApplicationSection>
        <Container>
          <LoadingIndicator>
            <svg width="50" height="50" viewBox="0 0 50 50">
              <circle 
                cx="25" 
                cy="25" 
                r="20" 
                fill="none" 
                stroke={theme.primary} 
                strokeWidth="3" 
                strokeLinecap="round"
              >
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  from="0 25 25" 
                  to="360 25 25" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dasharray" 
                  from="1, 150" 
                  to="90, 150" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
                <animate 
                  attributeName="stroke-dashoffset" 
                  from="0" 
                  to="-35" 
                  dur="1.5s" 
                  repeatCount="indefinite"
                />
              </circle>
            </svg>
          </LoadingIndicator>
        </Container>
      </ApplicationSection>
    );
  }
  
  if (projectError || error) {
    return (
      <ApplicationSection>
        <Container>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', color: theme.error, marginBottom: '1rem' }}>
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <h2 style={{ fontSize: '1.8rem', color: theme.text, marginBottom: '1.5rem' }}>
                {error || projectError}
              </h2>
              <Button variant="primary" to="/projects">
                <i className="fas fa-arrow-left"></i> {t('backToProjects')}
              </Button>
            </motion.div>
          </div>
        </Container>
      </ApplicationSection>
    );
  }
  
  if (!project) return null;
  
  return (
    <ApplicationSection>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Breadcrumbs theme={theme}>
            <Link to="/">{t('home')}</Link> <span>/</span> 
            <Link to="/projects">{t('projects')}</Link> <span>/</span> 
            <Link to={`/projects/${projectSlug}`}>{project.title}</Link> <span>/</span>
            <span>{t('apply')}</span>
          </Breadcrumbs>
          
          <SectionTitle
            title={t('applyToProject')}
            subtitle={t('applyToProjectSubtitle')}
          />
          
          <ProjectSummary theme={theme}>
            <ProjectImage src={project.image} />
            <ProjectInfo>
              <ProjectTitle theme={theme}>
                {project.title}
                <ProjectStatus theme={theme} status={project.status}>
                  {getStatusLabel(project.status)}
                </ProjectStatus>
              </ProjectTitle>
              
              <ProjectDescription theme={theme}>
                {project.short_description || project.description.substring(0, 150) + '...'}
              </ProjectDescription>
              
              <TechTagsContainer>
                {project.technologies.split(',').map((tech, i) => (
                  <TechTag key={i} theme={theme}>{tech.trim()}</TechTag>
                ))}
              </TechTagsContainer>
            </ProjectInfo>
          </ProjectSummary>
          
          <ApplicationForm theme={theme}>
            {applicationSuccess ? (
              <SuccessMessage
                theme={theme}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3 style={{ marginBottom: '0.5rem' }}>{t('applicationSubmitted')}</h3>
                <p>{t('applicationSubmittedDesc')}</p>
                <p style={{ marginTop: '1rem' }}>{t('redirectingToProject')}</p>
              </SuccessMessage>
            ) : (
              <form onSubmit={formik.handleSubmit}>
                <FormGroup>
                  <Label htmlFor="name" theme={theme}>{t('fullName')}</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                  />
                  {formik.touched.name && formik.errors.name && (
                    <ErrorMessage theme={theme}>{formik.errors.name}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email" theme={theme}>{t('email')}</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                  />
                  {formik.touched.email && formik.errors.email && (
                    <ErrorMessage theme={theme}>{formik.errors.email}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="phone" theme={theme}>{t('phone')}</Label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.phone}
                  />
                  {formik.touched.phone && formik.errors.phone && (
                    <ErrorMessage theme={theme}>{formik.errors.phone}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="motivation" theme={theme}>{t('motivation')}</Label>
                  <TextArea
                    id="motivation"
                    name="motivation"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.motivation}
                    placeholder={t('motivationPlaceholder')}
                  />
                  {formik.touched.motivation && formik.errors.motivation && (
                    <ErrorMessage theme={theme}>{formik.errors.motivation}</ErrorMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="experience" theme={theme}>{t('relevantExperience')}</Label>
                  <TextArea
                    id="experience"
                    name="experience"
                    theme={theme}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.experience}
                    placeholder={t('experiencePlaceholder')}
                  />
                  {formik.touched.experience && formik.errors.experience && (
                    <ErrorMessage theme={theme}>{formik.errors.experience}</ErrorMessage>
                  )}
                </FormGroup>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button
                    type="button"
                    variant="outline"
                    as={Link}
                    to={`/projects/${projectSlug}`}
                  >
                    {t('cancel')}
                  </Button>
                  
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={formik.isSubmitting}
                  >
                    {formik.isSubmitting ? (
                      <>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                        {t('submitting')}
                      </>
                    ) : (
                      t('submitApplication')
                    )}
                  </Button>
                </div>
              </form>
            )}
          </ApplicationForm>
        </motion.div>
      </Container>
    </ApplicationSection>
  );
};

export default ProjectApplicationPage;